import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import { handleError } from '../utils/error-handler';

export const useAwardBookingsStore = defineStore('awardBookings', {
    state: () => ({
        activeBookings: [],
        completedBookings: [],
        activeTab: 'active',
        loading: false,
        error: null,
        // Detail page state: { booking, messages, payment_requests }
        currentBooking: null,
        detailLoading: false,
        sendingMessage: false,
        submitting: false,
        payingPaymentRequestId: null,
        // Laravel 422 errors keyed by field, consumed by the form inputs.
        validationErrors: {},
        pagination: {
            active: {
                currentPage: 1,
                lastPage: 1,
                total: 0,
            },
            completed: {
                currentPage: 1,
                lastPage: 1,
                total: 0,
            },
        },
    }),
    getters: {
        currentBookings(state) {
            return state.activeTab === 'active'
                ? state.activeBookings
                : state.completedBookings;
        },
        hasBookings(state) {
            return (
                state.activeBookings.length > 0 ||
                state.completedBookings.length > 0
            );
        },
    },
    actions: {
        setActiveTab(tab) {
            this.activeTab = tab;
        },

        async fetchActiveBookings(params = {}) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get('/api/award-bookings/active', {
                    params,
                });
                this.activeBookings = response.data.bookings?.data || [];
                this.pagination.active = {
                    currentPage: response.data.bookings?.current_page || 1,
                    lastPage: response.data.bookings?.last_page || 1,
                    total: response.data.bookings?.total || 0,
                };
            } catch (error) {
                this.error = handleError(error);
                notify({
                    group: 'notifications',
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load active bookings',
                });
            } finally {
                this.loading = false;
            }
        },

        async fetchCompletedBookings(params = {}) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(
                    '/api/award-bookings/completed',
                    { params }
                );
                this.completedBookings = response.data.bookings?.data || [];
                this.pagination.completed = {
                    currentPage: response.data.bookings?.current_page || 1,
                    lastPage: response.data.bookings?.last_page || 1,
                    total: response.data.bookings?.total || 0,
                };
            } catch (error) {
                this.error = handleError(error);
                notify({
                    group: 'notifications',
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load completed bookings',
                });
            } finally {
                this.loading = false;
            }
        },

        async fetchAllBookings() {
            await Promise.all([
                this.fetchActiveBookings(),
                this.fetchCompletedBookings(),
            ]);
        },

        /**
         * Submit the request form. Returns { booking, checkout_url }; the
         * caller redirects the browser to checkout_url (Stripe). 422s land
         * in validationErrors for the form inputs.
         */
        async createBooking(data) {
            this.submitting = true;
            this.error = null;
            this.validationErrors = {};

            try {
                const response = await axios.post('/api/award-bookings', data);

                return response.data;
            } catch (error) {
                if (error.response?.status === 422) {
                    this.validationErrors = error.response.data.errors || {};
                    notify({
                        group: 'notifications',
                        type: 'error',
                        title: 'Check your answers',
                        text: 'Some required details are missing or invalid.',
                    });
                } else {
                    this.error = handleError(error);
                    notify({
                        group: 'notifications',
                        type: 'error',
                        title: 'Error',
                        text: 'Failed to submit your booking request',
                    });
                }
                throw error;
            } finally {
                this.submitting = false;
            }
        },

        async fetchBooking(uuid) {
            this.detailLoading = true;
            this.error = null;

            try {
                const response = await axios.get(`/api/award-bookings/${uuid}`);
                this.currentBooking = response.data;

                return response.data;
            } catch (error) {
                this.error = handleError(error);
                notify({
                    group: 'notifications',
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load your booking request',
                });
                throw error;
            } finally {
                this.detailLoading = false;
            }
        },

        /**
         * Upload one file to the booking thread ahead of sending. Resolves to
         * { id, name, size, mime_type }; the id goes out with the message.
         */
        async uploadAttachment(uuid, file, onProgress = () => {}) {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`/api/award-bookings/${uuid}/attachments`, formData, {
                onUploadProgress: (event) => {
                    if (event.total) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                },
            });

            return response.data;
        },

        async sendMessage(uuid, body, attachmentIds = []) {
            this.sendingMessage = true;

            try {
                const response = await axios.post(
                    `/api/award-bookings/${uuid}/messages`,
                    { body: body || null, attachment_ids: attachmentIds }
                );
                // The endpoint returns the refreshed detail payload.
                this.currentBooking = response.data;
            } catch (error) {
                this.error = handleError(error);
                notify({
                    group: 'notifications',
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to send your message',
                });
                throw error;
            } finally {
                this.sendingMessage = false;
            }
        },

        /**
         * Start Stripe Checkout for a payment request. Returns the checkout
         * URL; the caller performs the full-page redirect.
         */
        async payPaymentRequest(uuid, paymentRequestId) {
            this.payingPaymentRequestId = paymentRequestId;

            try {
                const response = await axios.post(
                    `/api/award-bookings/${uuid}/payment-requests/${paymentRequestId}/pay`
                );

                return response.data.checkout_url;
            } catch (error) {
                this.error = handleError(error);
                notify({
                    group: 'notifications',
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to start the payment — please try again',
                });
                throw error;
            } finally {
                this.payingPaymentRequestId = null;
            }
        },
    },
});
